import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';
import { updateAuthUser } from './AuthReducer';

// Define the initial state
const initialState = {
    profile: null,
    loading: false,
    error: null,
    successMessage: null,
};

// GET /api/admin/profile
export const getAdminProfile = createAsyncThunk(
    'profile/getAdminProfile',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/api/admin/profile');
            const user = response.data.data;

            // store in AuthReducer
            dispatch(updateAuthUser(user));

            // persist
            sessionStorage.setItem('admin-user', JSON.stringify(user));

            return user;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
        }
    }
);

// PATCH /api/users/profile
export const updateUserProfile = createAsyncThunk(
    'profile/updateUserProfile',
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            const res = await axiosInstance.patch('/api/admin/profile', formData);
            const updatedUser = res.data.data;

            // update Auth state
            dispatch(updateAuthUser(updatedUser));

            // persist
            sessionStorage.setItem('admin-user', JSON.stringify(updatedUser));

            return updatedUser;            
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
        }
    }
);

// PATCH /api/admin/profile/change-password
export const changeAdminPassword = createAsyncThunk(
    'profile/changeAdminPassword',
    async (passwordData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.patch('/api/admin/profile/change-password', passwordData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to change password');
        }
    }
);

const profileSlice = createSlice({
    name: 'profile',
    initialState,
    reducers: {
        clearProfileMessages: (state) => {
            state.error = null;
            state.successMessage = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getAdminProfile
            .addCase(getAdminProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAdminProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.profile = action.payload;
            })
            .addCase(getAdminProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // updateUserProfile
            .addCase(updateUserProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message || 'profile_updated_success';
                // Optionally update profile locally if the API doesn't return the full updated object
                if (action.payload) {
                    state.profile = { ...state.profile, ...action.payload };
                }
            })
            .addCase(updateUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // changeAdminPassword
            .addCase(changeAdminPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(changeAdminPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message || 'password_changed_success';
            })
            .addCase(changeAdminPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearProfileMessages } = profileSlice.actions;
export default profileSlice.reducer;